import { AxiosResponse } from "axios";
import { axiosInstance, MessageResponse } from "./common";

export enum AuditType {
  VIGILANCE_AUDIT = "VIGILANCE_AUDIT",
  RBIA = "RBIA",
  SPECIAL_AUDIT = "SPECIAL_AUDIT",
  IS_AUDIT = "IS_AUDIT",
  OTHERS = "OTHERS",
}

export const AUDIT_TYPE_LABELS: Record<AuditType, string> = {
  [AuditType.VIGILANCE_AUDIT]: "Vigilance Audit",
  [AuditType.RBIA]: "RBIA",
  [AuditType.SPECIAL_AUDIT]: "Special Audit",
  [AuditType.IS_AUDIT]: "IS Audit",
  [AuditType.OTHERS]: "Others",
};

export enum AuditorType {
  CONCURRENT_AUDITORS = "CONCURRENT_AUDITORS",
  RBIA_AUDITORS = "RBIA_AUDITORS",
  STATUTORY_AUDITORS = "STATUTORY_AUDITORS",
  VALUERS = "VALUERS",
  ADVOCATES = "ADVOCATES",
  STOCK_INVENTORY_AUDITORS = "STOCK_INVENTORY_AUDITORS",
  SURPRISE_AUDITORS = "SURPRISE_AUDITORS",
  OPERATIONAL_AUDITORS = "OPERATIONAL_AUDITORS",
  REGULATORY_AUDITORS = "REGULATORY_AUDITORS",
  REVENUE_INCOME_AUDITORS = "REVENUE_INCOME_AUDITORS",
}

export const AUDITOR_TYPE_LABELS: Record<AuditorType, string> = {
  [AuditorType.CONCURRENT_AUDITORS]: "Concurrent Auditors",
  [AuditorType.RBIA_AUDITORS]: "RBIA Auditors",
  [AuditorType.STATUTORY_AUDITORS]: "Statutory Auditors",
  [AuditorType.VALUERS]: "Valuers",
  [AuditorType.ADVOCATES]: "Advocates",
  [AuditorType.STOCK_INVENTORY_AUDITORS]: "Stock Inventory Auditors",
  [AuditorType.SURPRISE_AUDITORS]: "Surprise Auditors",
  [AuditorType.OPERATIONAL_AUDITORS]: "Operational Auditors",
  [AuditorType.REGULATORY_AUDITORS]: "Regulatory Auditors",
  [AuditorType.REVENUE_INCOME_AUDITORS]: "Revenue/Income Auditors",
};

export const AUDIT_TYPE_AUDITOR_MAPPING: Record<AuditType, AuditorType[]> = {
  [AuditType.VIGILANCE_AUDIT]: [
    AuditorType.SURPRISE_AUDITORS,
    AuditorType.OPERATIONAL_AUDITORS,
    AuditorType.REGULATORY_AUDITORS,
  ],
  [AuditType.RBIA]: [AuditorType.RBIA_AUDITORS],
  [AuditType.SPECIAL_AUDIT]: [
    AuditorType.STATUTORY_AUDITORS,
    AuditorType.VALUERS,
    AuditorType.ADVOCATES,
    AuditorType.STOCK_INVENTORY_AUDITORS,
  ],
  [AuditType.IS_AUDIT]: [
    AuditorType.CONCURRENT_AUDITORS,
    AuditorType.OPERATIONAL_AUDITORS,
    AuditorType.REGULATORY_AUDITORS,
  ],
  [AuditType.OTHERS]: [
    AuditorType.CONCURRENT_AUDITORS,
    AuditorType.STATUTORY_AUDITORS,
    AuditorType.VALUERS,
    AuditorType.ADVOCATES,
    AuditorType.STOCK_INVENTORY_AUDITORS,
    AuditorType.SURPRISE_AUDITORS,
    AuditorType.OPERATIONAL_AUDITORS,
    AuditorType.REGULATORY_AUDITORS,
    AuditorType.REVENUE_INCOME_AUDITORS,
  ],
};

export enum AuditRelationEntityType {
  ACCOUNT = "ACCOUNT",
  CREDIT_ACCOUNT = "CREDIT_ACCOUNT",
}

export const AUDIT_RELATION_ENTITY_TYPE_LABELS: Record<AuditRelationEntityType, string> = {
  [AuditRelationEntityType.ACCOUNT]: "Account",
  [AuditRelationEntityType.CREDIT_ACCOUNT]: "Credit Account",
};

export interface AuditEntityRelation {
  entity_type: AuditRelationEntityType;
  entity_id: string;
}

export interface CaseAudit {
  audit_id: string;
  audit_type: AuditType;
  auditor_type: AuditorType;
  auditor_name?: string | null;
  target_accounts?: AuditEntityRelation[] | null;
  audit_submission_date: string;
  adverse_observations?: string | null;
}

export interface GetCaseAuditsResponse {
  audits: CaseAudit[];
}

export interface UpsertCaseAuditsRequest {
  audits: CaseAudit[];
}

export const CaseAuditsAPI = {
  get: async (case_id: string): Promise<GetCaseAuditsResponse> => {
    const response: AxiosResponse<GetCaseAuditsResponse> =
      await axiosInstance.get(`v1/cases/${case_id}/audits`);

    return response.data;
  },

  upsert: async (
    case_id: string,
    request: UpsertCaseAuditsRequest,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(`v1/cases/${case_id}/audits`, request);

    return response.data;
  },
};

export default CaseAuditsAPI;